import { apiClient } from "../api/apiClient";

// ==========================================
// TYPES
// ==========================================

export interface FlashSale {

  flashSaleID: number;

  title: string;

  discount: number;

  startTime: string;

  endTime: string;

  status: "upcoming" | "live" | "ended";

  banner: string;

  saleLabel?: string;

  saveUpTo: number;

  saleImageUrl?: string;

  saleImageFile?: File | null;

  isActive: boolean;

  // ======================================
  // PRODUCT IDS
  // ======================================

  productIDs?: number[];

  // ======================================
  // PRODUCT NAMES
  // ======================================

  products?: Product[];
}

// ==========================================
// PRODUCT TYPE
// ==========================================

export interface Product {

  productID: number;

  productName: string;
}

// ==========================================
// API RESPONSE
// ==========================================

export interface ApiResponse<T> {

  loggingHistoryID?: number;

  statusCode: number;

  isSuccess: boolean;

  message: string;

  errors?: string[];

  data?: T;

  dataList?: T[];
}

// ==========================================
// API BASE URL
// ==========================================

const API_URL =
  "/FlashSales";

// ==========================================
// FLASH SALE SERVICE
// ==========================================

export const flashSaleService = {
getProducts: async (): Promise<Product[]> => {

  const response =
    await apiClient.get(
      `${API_URL}/GetProduct`
    );

  console.log(
    "RAW PRODUCT RESPONSE:",
    response.data
  );

  // ✅ API returns:
  // {
  //   statusCode,
  //   success,
  //   message,
  //   data:[]
  // }

  return Array.isArray(
    response.data.data
  )
    ? response.data.data
    : [];
},
  // ======================================
  // GET ALL
  // ======================================

  getAll: async (): Promise<FlashSale[]> => {

    const response =
      await apiClient.get<
        ApiResponse<FlashSale>
      >(
        `${API_URL}/GetAll`
      );

    const result =
      response.data;

    console.log(
      "Flash Sale Data List:",
      result.dataList
    );

    if (!result.isSuccess) {

      throw new Error(
        result.message
      );
    }

    return result.dataList || [];
  },

  // ======================================
  // GET BY ID
  // ======================================

  getByID: async (
    id: number
  ): Promise<FlashSale> => {

    const response =
      await apiClient.get<
        ApiResponse<FlashSale>
      >(
        `${API_URL}/GetByID/${id}`
      );

    const result =
      response.data;

    console.log(
      "Flash Sale Data:",
      result.data
    );

    if (!result.isSuccess) {

      throw new Error(
        result.message
      );
    }

    return result.data!;
  },

  // ======================================
  // CREATE
  // ======================================

  create: async (
    form: any
  ): Promise<FlashSale> => {

    const formData =
      new FormData();

    formData.append(
      "Title",
      form.title
    );

    formData.append(
      "Discount",
      form.discount.toString()
    );

    formData.append(
      "StartTime",
      form.startTime
    );

    formData.append(
      "EndTime",
      form.endTime
    );

    formData.append(
      "Status",
      form.status
    );

    formData.append(
      "Banner",
      form.banner
    );

    formData.append(
      "SaleLabel",
      form.saleLabel || ""
    );

    formData.append(
      "SaveUpTo",
      form.saveUpTo.toString()
    );

    // ======================================
    // IMAGE
    // ======================================

    if (form.saleImageFile) {

      formData.append(
        "SaleImageFile",
        form.saleImageFile
      );
    }

    // ======================================
    // PRODUCT IDS
    // ======================================

    if (
      form.productIDs &&
      form.productIDs.length > 0
    ) {

      form.productIDs.forEach(
        (x: number) => {

          formData.append(
            "ProductIDs",
            x.toString()
          );
        });
    }

    const response =
      await apiClient.post<
        ApiResponse<FlashSale>
      >(
        `${API_URL}/Create`,
        formData,
        {
          headers: {
            "Content-Type":
              "multipart/form-data"
          }
        }
      );

    const result =
      response.data;

    if (!result.isSuccess) {

      throw new Error(
        result.message
      );
    }

    return result.data!;
  },

  // ======================================
  // UPDATE
  // ======================================

  update: async (
    id: number,
    form: any
  ): Promise<FlashSale> => {

    const formData =
      new FormData();

    formData.append(
      "FlashSaleID",
      id.toString()
    );

    formData.append(
      "Title",
      form.title
    );

    formData.append(
      "Discount",
      form.discount.toString()
    );

    formData.append(
      "StartTime",
      form.startTime
    );

    formData.append(
      "EndTime",
      form.endTime
    );

    formData.append(
      "Status",
      form.status
    );

    formData.append(
      "Banner",
      form.banner
    );

    formData.append(
      "SaleLabel",
      form.saleLabel || ""
    );

    formData.append(
      "SaveUpTo",
      form.saveUpTo.toString()
    );

    // ======================================
    // IMAGE
    // ======================================

    if (form.saleImageFile) {

      formData.append(
        "SaleImageFile",
        form.saleImageFile
      );
    }

    // ======================================
    // PRODUCT IDS
    // ======================================

    if (
      form.productIDs &&
      form.productIDs.length > 0
    ) {

      form.productIDs.forEach(
        (x: number) => {

          formData.append(
            "ProductIDs",
            x.toString()
          );
        });
    }

    const response =
      await apiClient.put<
        ApiResponse<FlashSale>
      >(
        `${API_URL}/Update`,
        formData,
        {
          headers: {
            "Content-Type":
              "multipart/form-data"
          }
        }
      );

    const result =
      response.data;

    if (!result.isSuccess) {

      throw new Error(
        result.message
      );
    }

    return result.data!;
  },

  // ======================================
  // DELETE
  // ======================================

  delete: async (
    id: number
  ): Promise<boolean> => {

    const response =
      await apiClient.delete<
        ApiResponse<FlashSale>
      >(
        `${API_URL}/Delete/${id}`
      );

    const result =
      response.data;

    if (!result.isSuccess) {

      throw new Error(
        result.message
      );
    }

    return true;
  }
};